import React from "react";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { Body } from "../../components/text";
import {
  StaticContentCopy,
  StaticContentTopicTitle,
  StaticContentTopicSubtitle,
} from "../../components/atoms/styledText";
import { useUser } from "../../MVVM/Hooks/useUser";
import { HiOutlineCheck } from "react-icons/hi";

const CommandCode = styled.code`
  background-color: ${({ theme }) => theme.colors.nedLight || "#1a2630"};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.envy};
  font-family: "Courier New", Courier, monospace;
  font-size: 0.9em;
  padding: 2px 6px;
`;

const CommandRow = styled(Flex)`
  align-items: baseline;
  gap: 8px;
  margin-bottom: 10px;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.envy};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.8;
  }
`;

const InlineLink = styled(StyledLink)`
  display: inline;
`;

const StepHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const CheckBadge = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  border-radius: 50%;
  height: 20px;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
`;

const ImageFrame = styled(Box)`
  border: 1px solid
    ${({ theme }) => theme.colors.envyAlpha20 || "rgba(77, 189, 148, 0.2)"};
  border-radius: 12px;
  box-shadow: 0 0 20px
    ${({ theme }) => theme.colors.envyAlpha10 || "rgba(77, 189, 148, 0.1)"};
  margin-bottom: 10px;
  margin-top: 16px;
  max-width: 400px;
  overflow: hidden;
  padding: 3px;
`;

const SectionImage = styled.img`
  border-radius: 9px;
  display: block;
  width: 100%;
`;

const SectionDivider = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  margin-bottom: 40px;
  margin-top: 10px;
  width: 100%;
`;

export function AllstarBotContent() {
  const { allstarUser } = useUser();
  const discordLinked = !!allstarUser?.user?.discord?.username;

  return (
    <StaticContentCopy maxWidth={6} data-testid="allstarbot">
      {/* Getting Started */}
      <StaticContentTopicTitle mb={6}>Getting Started</StaticContentTopicTitle>

      <Box mb={8}>
        <StepHeader mb={3}>
          <StaticContentTopicSubtitle>
            Link Your Discord Account
          </StaticContentTopicSubtitle>
          {discordLinked && (
            <CheckBadge>
              <HiOutlineCheck size={14} color="#fff" />
            </CheckBadge>
          )}
        </StepHeader>
        <Body>
          {discordLinked ? (
            <>
              Your Discord account ({allstarUser.user.discord.username}) is
              linked. You're good to go!
            </>
          ) : (
            <>
              To use AllstarBot, your Discord account needs to be connected to
              Allstar. If you haven't already,{" "}
              <InlineLink href="/connectedaccounts">
                link your Discord account
              </InlineLink>{" "}
              to get started.
            </>
          )}
        </Body>
      </Box>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Add AllstarBot to Your Server
        </StaticContentTopicSubtitle>
        <Body>
          Add AllstarBot to your Discord server from the{" "}
          <InlineLink
            href="https://discord.com/application-directory/580572502288498700"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord App Directory
          </InlineLink>
          . You'll need Manage Server permissions to add the bot.
        </Body>
      </Box>

      <SectionDivider />

      {/* Match Notifications */}
      <StaticContentTopicTitle mb={6}>
        Match Notifications
      </StaticContentTopicTitle>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Get Notified When Matches Are Ready
        </StaticContentTopicSubtitle>
        <Body>
          Never miss a match again. Use{" "}
          <CommandCode>/match-notify on</CommandCode> in the channel where you'd
          like notifications to appear. AllstarBot will post a message whenever
          a match is ready for anyone in your server who has linked their
          account.
        </Body>
      </Box>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Want Personal Notifications?
        </StaticContentTopicSubtitle>
        <Body>
          If you'd rather get notified directly, use{" "}
          <CommandCode>/match-notify dm-on</CommandCode> in any server where
          AllstarBot is active. You'll receive a personal DM whenever your
          matches are ready — no need to keep an eye on a channel.
        </Body>
        <ImageFrame>
          <SectionImage
            src="https://media.allstar.gg/static/web/match-notification.png"
            alt="AllstarBot match notification example"
          />
        </ImageFrame>
      </Box>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Notification Commands
        </StaticContentTopicSubtitle>
        <Box>
          <CommandRow>
            <CommandCode>/match-notify on</CommandCode>
            <Body>Enables match-ready notifications for your server</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/match-notify off</CommandCode>
            <Body>Disables match-ready notifications for your server</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/match-notify channel #channel</CommandCode>
            <Body>Changes which channel notifications are posted to</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/match-notify dm-on</CommandCode>
            <Body>Get match-ready notifications as a DM</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/match-notify dm-off</CommandCode>
            <Body>Stop receiving match-ready DMs</Body>
          </CommandRow>
        </Box>
      </Box>

      <SectionDivider />

      {/* Clip Sharing */}
      <StaticContentTopicTitle mb={6}>Clip Sharing</StaticContentTopicTitle>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Share Clips Automatically
        </StaticContentTopicSubtitle>
        <Body>
          AllstarBot can automatically share clips to your Discord server
          whenever they're created. Use <CommandCode>/share all</CommandCode> to
          enable clip sharing for all linked users in your server, or{" "}
          <CommandCode>/share role @role-name</CommandCode> to limit it to
          members with a specific role.
        </Body>
        <ImageFrame>
          <SectionImage
            src="https://media.allstar.gg/static/web/clip-notification.png"
            alt="AllstarBot clip sharing example"
          />
        </ImageFrame>
      </Box>

      <Box mb={8}>
        <StaticContentTopicSubtitle mb={3}>
          Clip Sharing Commands
        </StaticContentTopicSubtitle>
        <Box>
          <CommandRow>
            <CommandCode>/share all</CommandCode>
            <Body>Post clips for all known Discord users in your server</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/share role @role-name</CommandCode>
            <Body>Post clips only for users matching a specific role</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/share off</CommandCode>
            <Body>Turns off clip sharing for the current channel</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/gamefilter game</CommandCode>
            <Body>
              Filter clips by game (CS2, Dota 2, League of Legends, Fortnite, or
              all)
            </Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/mention-me on</CommandCode>
            <Body>Get mentioned when AllstarBot posts your clips</Body>
          </CommandRow>
          <CommandRow>
            <CommandCode>/mention-me off</CommandCode>
            <Body>Stop getting mentioned when your clips are posted</Body>
          </CommandRow>
        </Box>
      </Box>
    </StaticContentCopy>
  );
}
